import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { safeStorage } from '../../lib/safe-storage';
import { apiClient } from '@/lib/api-client';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Progress } from '../ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Badge } from '../ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '../ui/dropdown-menu';
import {
  ChevronLeft,
  ChevronRight,
  Save,
  Download,
  Eye,
  FileText,
  User,
  GraduationCap,
  Briefcase,
  Settings,
  FolderOpen,
  ArrowLeft,
  Users
} from 'lucide-react';
// Removed Accordion import - using direct form rendering

// Import form components
import PersonalInfoForm from './PersonalInfoForm';
import SummaryForm from './SummaryForm';
import EducationForm from './EducationForm';
import ExperienceForm from './ExperienceForm';
import SkillsForm from './SkillsForm';
import ProjectsForm from './ProjectsForm';
import ImmediateContactForm, { ImmediateContact } from './ImmediateContactForm';
import ReferencesForm, { Reference } from './ReferencesForm';
import ResumePreview from './ResumePreview';
import { useApp } from '@/pages/providers/AppProvider';

// Types
interface PersonalInfo {
  fullName: string;
  email: string;
  phone: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  country: string;
  linkedin: string;
  website: string;
  github: string;
}

interface Education {
  id: string;
  institution: string;
  degree: string;
  fieldOfStudy: string;
  startDate: string;
  endDate: string;
  isCurrentlyStudying: boolean;
  gpa: string;
  description: string;
  location: string;
}

interface Experience {
  id: string;
  jobTitle: string;
  company: string;
  location: string;
  startDate: string;
  endDate: string;
  isCurrentJob: boolean;
  description: string;
  achievements: string[];
}

interface Skill {
  id: string;
  name: string;
  category: string;
  level: 'Beginner' | 'Intermediate' | 'Advanced' | 'Expert';
}

interface Project {
  id: string;
  name: string;
  description: string;
  technologies: string[];
  startDate: string;
  endDate: string;
  projectUrl: string;
  githubUrl: string;
  highlights: string[];
}

interface Language {
  id: string;
  name: string;
  proficiency: 'Basic' | 'Intermediate' | 'Advanced' | 'Fluent' | 'Native';
}

interface ResumeData {
  title: string;
  personalInfo: PersonalInfo;
  summary: string;
  education: Education[];
  experience: Experience[];
  skills: Skill[];
  languages: Language[];
  projects: Project[];
  template: string;
  experienceStatus?: 'experienced' | 'fresher' | 'internship' | 'student';
  immediateContact?: ImmediateContact;
  references?: Reference[];
}

const steps = [
  { id: 'personal', label: 'Personal Info', icon: User },
  { id: 'summary', label: 'Summary', icon: FileText },
  { id: 'experience', label: 'Experience', icon: Briefcase },
  { id: 'education', label: 'Education', icon: GraduationCap },
  { id: 'skills', label: 'Skills', icon: Settings },
  { id: 'projects', label: 'Projects', icon: FolderOpen },
  { id: 'references', label: 'References', icon: Users },
  { id: 'contact', label: 'Immediate Contact', icon: Users }
];

const ResumeBuilder: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { syncResumes } = useApp();
  const [currentStep, setCurrentStep] = useState(0);
  const [isLoading, setIsLoading] = useState(!!id);
  const [resumeData, setResumeData] = useState<ResumeData>({
    title: 'My Resume',
    personalInfo: {
      fullName: '',
      email: '',
      phone: '',
      address: '',
      city: '',
      state: '',
      zipCode: '',
      country: '',
      linkedin: '',
      website: '',
      github: ''
    },
    summary: '',
    education: [{
      id: '1',
      institution: '',
      degree: '',
      fieldOfStudy: '',
      startDate: '',
      endDate: '',
      isCurrentlyStudying: false,
      gpa: '',
      description: '',
      location: ''
    }],
    experience: [{
      id: '1',
      jobTitle: '',
      company: '',
      location: '',
      startDate: '',
      endDate: '',
      isCurrentJob: false,
      description: '',
      achievements: ['']
    }],
    skills: [],
    languages: [],
    projects: [],
    template: 'modern',
    experienceStatus: 'experienced',
    immediateContact: {
      name: '',
      phone: '',
      relation: ''
    },
    references: []
  });

  const [isSaving, setIsSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [isNewResume, setIsNewResume] = useState(true);

  const isPersonalInfoValid = () => {
    const { fullName, email, phone, address, city } = resumeData.personalInfo || {} as any;
    return Boolean(fullName && String(fullName).trim()) &&
      Boolean(email && String(email).trim()) &&
      Boolean(phone && String(phone).trim()) &&
      Boolean(address && String(address).trim()) &&
      Boolean(city && String(city).trim());
  };

  const isExperienceValid = () => {
    const status = resumeData.experienceStatus || 'experienced';
    if (status !== 'experienced') return true;
    const first = resumeData.experience && resumeData.experience[0];
    if (!first) return false;
    return Boolean(first.jobTitle && String(first.jobTitle).trim()) && Boolean(first.company && String(first.company).trim());
  };

  const isEducationValid = () => {
    const first = resumeData.education && resumeData.education[0];
    if (!first) return false;
    return Boolean(first.institution && String(first.institution).trim()) && Boolean(first.degree && String(first.degree).trim());
  };

  const isContactValid = () => {
    const { name, phone, relation } = resumeData.immediateContact || {} as any;
    // Making it optional but if filled should be valid? Or just optional?
    // User asked to "add section... from where user can fill". 
    // Usually immediate contact is good to have. I'll make it optional for blockage but part of progress.
    // If we want to enforce it, we return checked booleans. 
    // For now, let's treat it as valid always to not block navigation unless user fills partial info? 
    // Let's assume it is NOT blocking 
    return true;
  };

  const requiredStepIsValid = (index: number) => {
    switch (index) {
      case 0: return isPersonalInfoValid();
      case 2: return isExperienceValid();
      case 3: return isEducationValid();
      default: return true;
    }
  };

  const canNavigateToStep = (targetIndex: number) => {
    for (let i = 0; i < targetIndex; i++) {
      if (!requiredStepIsValid(i)) return false;
    }
    return true;
  };

  const calculateProgress = () => {
    let score = 0;

    // 1. Personal Info (15%)
    const { fullName, email, phone } = resumeData.personalInfo || {};
    if (fullName?.trim()) score += 5;
    if (email?.trim()) score += 5;
    if (phone?.trim()) score += 5;

    // 2. Summary (10%)
    if (resumeData.summary?.trim()) score += 10;

    // 3. Experience (25%)
    const status = resumeData.experienceStatus || 'experienced';
    const isExperienced = status === 'experienced';
    const hasExp = !isExperienced || (resumeData.experience?.length > 0 && resumeData.experience.some(e => e.jobTitle?.trim() && e.company?.trim()));
    if (hasExp) score += 25;

    // 4. Education (25%)
    const hasEdu = resumeData.education?.length > 0 && resumeData.education.some(e => e.institution?.trim() && e.degree?.trim());
    if (hasEdu) score += 25;

    // 5. Skills (15%)
    if (resumeData.skills?.length > 0) score += 15;

    // 6. Projects (10%)
    if (resumeData.projects?.length > 0) score += 10;

    // 7. Immediate Contact (5%)
    const { name: contactName, phone: contactPhone } = resumeData.immediateContact || {};
    if (contactName?.trim() && contactPhone?.trim()) score += 5;

    // 8. References (Optional - bonus 5%)
    if (resumeData.references && resumeData.references.length > 0) score += 5;

    return Math.min(100, score);
  };

  // Auto-save functionality...
  // useEffect(() => {
  //   const autoSave = setTimeout(() => {
  //     handleSave(true); // Silent save
  //   }, 30000); // Auto-save every 30 seconds
  //   return () => clearTimeout(autoSave);
  // }, [resumeData]);

  useEffect(() => {
    loadUserData();
  }, []);

  // Load existing resume if editing
  useEffect(() => {
    if (id) {
      loadExistingResume();
    } else {
      setIsLoading(false);
      setIsNewResume(true);
    }
  }, [id]);

  const loadExistingResume = async () => {
    try {
      const token = safeStorage.getItem('megajobnepal_auth_token');
      if (!token || !id) {
        setIsLoading(false);
        return;
      }
      const json = await apiClient.request(`/resumes/${id}`);
      const data = json?.data || json;
      setResumeData({
        title: data.title || 'My Resume',
        personalInfo: data.personalInfo || resumeData.personalInfo,
        summary: data.summary || '',
        education: Array.isArray(data.education) ? data.education : [],
        experience: Array.isArray(data.experience) ? data.experience : [],
        skills: Array.isArray(data.skills) ? data.skills : [],
        languages: Array.isArray(data.languages) ? data.languages : [],
        projects: Array.isArray(data.projects) ? data.projects : [],
        template: data.template || 'modern',
        immediateContact: data.immediateContact || { name: '', phone: '', relation: '' },
        references: Array.isArray(data.references) ? data.references : []
      });
      setIsNewResume(false);
      toast.success('Resume loaded successfully!', { id: 'resume-loaded' });
    } catch (error) {
      console.error('Error loading resume:', error);
      toast.error('Failed to load resume');
    } finally {
      setIsLoading(false);
    }
  };

  const loadUserData = async () => {
    try {
      const token = safeStorage.getItem('megajobnepal_auth_token');
      if (!token) return;

      const result = await apiClient.request('/users/profile');
      const user = (result as any)?.user || result;
      setResumeData(prev => ({
        ...prev,
        personalInfo: {
          ...prev.personalInfo,
          fullName: user?.full_name || prev.personalInfo.fullName,
          email: user?.email || prev.personalInfo.email,
          phone: user?.phone || prev.personalInfo.phone,
          location: user?.location || prev.personalInfo.address
        }
      }));
    } catch (error) {
      /* noop */
    }
  };

  const handleSave = async (silent = false) => {
    if (!silent) setIsSaving(true);
    try {
      if (!id) throw new Error('Resume ID is required');
      const token = safeStorage.getItem('megajobnepal_auth_token');
      if (!token) throw new Error('Authentication required');
      const response = await apiClient.request(`/resumes/${id}`, { method: 'PUT', body: JSON.stringify(resumeData) });
      setIsNewResume(false);
      // Sync resumes to AppProvider so dashboard updates
      try { await syncResumes(); } catch (e) { console.error('Sync failed:', e); }

      if (!silent) toast.success('Resume saved successfully!');
    } catch (error: any) {
      console.error('Error saving resume:', error);
      if (!silent) toast.error(error?.message || 'Failed to save resume. Please try again.');
    } finally {
      if (!silent) setIsSaving(false);
    }
  };

  const handleDownload = async () => {
    try {
      await handleSave(true);
      setShowPreview(true);
      setTimeout(() => {
        window.print();
        setTimeout(() => setShowPreview(false), 500);
      }, 300);
    } catch (error) {
      console.error('Error downloading resume:', error);
    }
  };

  const handleDownloadDocx = async () => {
    try {
      const token = safeStorage.getItem('megajobnepal_auth_token');
      if (!token || !id) throw new Error('No authentication token');
      const blob = await apiClient.requestBlob(`/resumes/${id}/export/docx?template=${encodeURIComponent(resumeData.template)}`);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${resumeData.personalInfo.fullName || resumeData.title || 'resume'}.docx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error downloading resume DOCX:', error);
      alert('Failed to download Word file. Please try again.');
    }
  };

  if (showPreview) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="container mx-auto px-4 py-8">
          <style>{`
            @media print {
              /* Reset global layout constraints */
              html, body, #root, .flex.h-screen, .flex-1, main { 
                background: white !important; 
                margin: 0 !important; 
                padding: 0 !important; 
                height: auto !important;
                min-height: auto !important;
                overflow: visible !important;
                display: block !important;
                width: 100% !important;
                float: none !important;
                position: static !important;
              }
              
              /* Force hide all UI elements including the Template Selector Card */
              nav, header:not(#resume-print-area header), footer, aside, 
              .no-print, .print\\:hidden, button, .flex.justify-between.items-center.mb-6,
              [role="navigation"], .rounded-lg.border.bg-card { 
                display: none !important; 
              }
              
              /* Explicitly hide the Template Selector section */
              #resume-print-area - div:first-child, .print-hide, .print\\:hidden {
                 display: none !important;
              }

              #resume-print-area { 
                display: block !important;
                visibility: visible !important;
                margin: 0 !important;
                padding: 0 !important;
                width: 100% !important;
                max-width: none !important;
                box-shadow: none !important;
                transform: none !important;
              }

              /* Keep headers with their following content */
              h2, h3, .text-2xl, .text-xl { 
                break-after: avoid !important;
                page-break-after: avoid !important;
              }

              section, .resume-section { 
                page-break-inside: auto !important;
                break-inside: auto !important;
                margin-bottom: 1.5rem !important;
                display: block !important;
              }

              /* Preserve flex and grid in resume */
              #resume-print-area .flex { display: flex !important; }
              #resume-print-area .grid { display: grid !important; }
              #resume-print-area .Badge { display: inline-flex !important; }
            }
            @page { 
              size: A4; 
              margin: 20mm 15mm 20mm 15mm; 
            }
          `}</style>
          <div className="flex justify-between items-center mb-6">
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => navigate('/jobseeker-dashboard/resume')}
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Dashboard
              </Button>
              <Button
                variant="outline"
                onClick={() => setShowPreview(false)}
              >
                <ChevronLeft className="h-4 w-4 mr-2" />
                Back to Editor
              </Button>
            </div>
            <div className="flex gap-2">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button disabled={isSaving}>
                    <Save className="h-4 w-4 mr-2" />
                    {isSaving ? 'Saving...' : 'Save'}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={async () => { await handleSave(true); await handleDownload(); }}>
                    <Download className="h-4 w-4 mr-2" />
                    Download PDF
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={async () => { await handleSave(true); await handleDownloadDocx(); }}>
                    <Download className="h-4 w-4 mr-2" />
                    Download Word
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
          <ResumePreview
            data={resumeData}
            onTemplateChange={(template) => setResumeData(prev => ({ ...prev, template }))}
          />
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Progress value={(currentStep / steps.length) * 100} className="mb-4" />
          <p className="text-gray-600">Loading your resume...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-8">
        {/* Header with Back Button */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Button
              variant="outline"
              onClick={() => navigate('/jobseeker-dashboard/resume')}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to Dashboard
            </Button>
            <div>
              <h1 className="text-3xl font-bold">Resume Builder</h1>
              <p className="text-gray-600">Completion: {calculateProgress()}%</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setShowPreview(true)}>
              <Eye className="h-4 w-4 mr-2" />
              Preview
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button disabled={isSaving}>
                  <Save className="h-4 w-4 mr-2" />
                  {isSaving ? 'Saving...' : 'Save'}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={async () => { await handleSave(true); await handleDownload(); }}>
                  <Download className="h-4 w-4 mr-2" />
                  Download PDF
                </DropdownMenuItem>
                <DropdownMenuItem onClick={async () => { await handleSave(true); await handleDownloadDocx(); }}>
                  <Download className="h-4 w-4 mr-2" />
                  Download Word
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        <div className="md:hidden mb-4">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-sm font-medium text-gray-600">Resume Section</h2>
            <Badge className="bg-blue-600 text-white">{currentStep + 1}/{steps.length}</Badge>
          </div>
          <Select
            value={String(currentStep)}
            onValueChange={async (v) => {
              const index = Number(v);
              const disabled = !canNavigateToStep(index);
              if (disabled) {
                const blocking = [0, 2, 3].find(i => i < index && !requiredStepIsValid(i));
                const label = typeof blocking === 'number' ? steps[blocking].label : 'previous section';
                toast.error(`Please complete required fields in ${label}`, { id: 'resume-step-block' });
                return;
              }
              if (requiredStepIsValid(currentStep)) {
                await handleSave(true);
              }
              setCurrentStep(index);
            }}
          >
            <SelectTrigger className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {steps.map((s, i) => (
                <SelectItem key={s.id} value={String(i)}>{s.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
          {/* Sidebar Navigation */}
          <div className="hidden md:block md:col-span-1">
            <Card className="sticky top-6">
              <CardHeader>
                <CardTitle className="text-sm font-medium text-gray-600">Resume Sections</CardTitle>
              </CardHeader>
              <CardContent className="p-3">
                <div className="space-y-1">
                  {steps.map((step, index) => {
                    const Icon = step.icon;
                    const isActive = currentStep === index;
                    const isCompleted = index < currentStep;

                    const disabled = !canNavigateToStep(index);
                    return (
                      <button
                        key={step.id}
                        onClick={async () => {
                          if (disabled) {
                            const blocking = [0, 2, 3].find(i => i < index && !requiredStepIsValid(i));
                            const label = typeof blocking === 'number' ? steps[blocking].label : 'previous section';
                            toast.error(`Please complete required fields in ${label}`, { id: 'resume-step-block' });
                            return;
                          }
                          if (requiredStepIsValid(currentStep)) {
                            await handleSave(true);
                          }
                          setCurrentStep(index);
                        }}
                        disabled={disabled}
                        className={`w-full flex items-center justify-between p-3 rounded-lg transition-all ${isActive
                          ? 'bg-blue-600 text-white shadow-lg'
                          : isCompleted
                            ? 'bg-green-50 text-green-700 hover:bg-green-100'
                            : 'hover:bg-gray-50 text-gray-700'
                          } ${disabled ? 'cursor-not-allowed opacity-50' : ''}`}
                      >
                        <div className="flex items-center gap-3">
                          <Icon className={`h-5 w-5 ${isActive ? 'text-white' : isCompleted ? 'text-green-600' : 'text-gray-400'
                            }`} />
                          <span className="text-sm font-medium">{step.label}</span>
                        </div>
                        {isCompleted && (
                          <Badge className="bg-green-600 text-white">✓</Badge>
                        )}
                        {isActive && (
                          <Badge className="bg-blue-700">●</Badge>
                        )}
                      </button>
                    );
                  })}
                </div>

                {/* Progress */}
                <div className="mt-6 p-3 bg-gray-50 rounded-lg">
                  <div className="flex justify-between text-xs text-gray-600 mb-2">
                    <span>Profile Strength</span>
                    <span>{calculateProgress()}%</span>
                  </div>
                  <Progress value={calculateProgress()} className="h-2" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Main Content */}
          <div className="md:col-span-4">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-2xl">{steps[currentStep].label}</CardTitle>
                    <p className="text-sm text-gray-500 mt-1">Step {currentStep + 1} of {steps.length}</p>
                  </div>
                  <Badge variant="outline" className="text-lg px-4 py-2">
                    {steps[currentStep].label}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent>
                {currentStep === 0 && (
                  <PersonalInfoForm
                    data={resumeData.personalInfo}
                    onChange={(data) => setResumeData(prev => ({ ...prev, personalInfo: data }))}
                  />
                )}

                {currentStep === 1 && (
                  <SummaryForm
                    data={resumeData.summary}
                    onChange={(data) => setResumeData(prev => ({ ...prev, summary: data }))}
                  />
                )}

                {currentStep === 2 && (
                  <ExperienceForm
                    data={resumeData.experience}
                    status={resumeData.experienceStatus || 'experienced'}
                    onStatusChange={(status) => setResumeData(prev => ({ ...prev, experienceStatus: status }))}
                    onChange={(data) => setResumeData(prev => ({ ...prev, experience: data }))}
                  />
                )}

                {currentStep === 3 && (
                  <EducationForm
                    data={resumeData.education}
                    onChange={(data) => setResumeData(prev => ({ ...prev, education: data }))}
                  />
                )}

                {currentStep === 4 && (
                  <SkillsForm
                    data={resumeData.skills}
                    languages={resumeData.languages}
                    onChange={(data) => setResumeData(prev => ({ ...prev, skills: data }))}
                    onLanguagesChange={(data) => setResumeData(prev => ({ ...prev, languages: data }))}
                  />
                )}

                {currentStep === 5 && (
                  <ProjectsForm
                    data={resumeData.projects}
                    onChange={(data) => setResumeData(prev => ({ ...prev, projects: data }))}
                  />
                )}

                {currentStep === 6 && (
                  <ReferencesForm
                    data={resumeData.references || []}
                    onChange={(data) => setResumeData(prev => ({ ...prev, references: data }))}
                  />
                )}

                {currentStep === 7 && (
                  <ImmediateContactForm
                    data={resumeData.immediateContact || { name: '', phone: '', relation: '' }}
                    onChange={(data) => setResumeData(prev => ({ ...prev, immediateContact: data }))}
                  />
                )}

                {/* Navigation Buttons */}
                <div className="flex flex-col sm:flex-row justify-between gap-3 mt-8 pt-6 border-t">
                  <Button
                    variant="outline"
                    onClick={() => setCurrentStep(Math.max(currentStep - 1, 0))}
                    disabled={currentStep === 0}
                    className="w-full sm:w-auto"
                    size="lg"
                  >
                    <ChevronLeft className="h-4 w-4 mr-2" />
                    Previous
                  </Button>

                  <div className="flex gap-2 w-full sm:w-auto">

                    <Button
                      onClick={() => handleSave(false)}
                      variant="outline"
                      disabled={isSaving}
                      className="flex-1 sm:flex-none"
                    >
                      <Save className="h-4 w-4 mr-2" />
                      {isSaving ? 'Saving...' : 'Save'}
                    </Button>
                  </div>

                  {currentStep < steps.length - 1 ? (
                    <Button
                      onClick={async () => {
                        if (!requiredStepIsValid(currentStep)) {
                          toast.error(`Please complete required fields in ${steps[currentStep].label}`, { id: 'resume-step-block' });
                          return;
                        }
                        await handleSave(true);
                        setCurrentStep(Math.min(currentStep + 1, steps.length - 1));
                      }}
                      disabled={!requiredStepIsValid(currentStep)}
                      className="w-full sm:w-auto"
                      size="lg"
                    >
                      Next
                      <ChevronRight className="h-4 w-4 ml-2" />
                    </Button>
                  ) : (
                    <Button
                      onClick={() => setShowPreview(true)}
                      className="w-full sm:w-auto"
                      size="lg"
                    >
                      Preview Resume
                      <Eye className="h-4 w-4 ml-2" />
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResumeBuilder;
